import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import useTranslate from '@/hooks/use-translate'
import { commandBarSectionTitleSx } from './commandBar.styles'
import { CommandBarItemRow } from './CommandBarItemRow'
import type { CommandBarSection } from './types'

type CommandBarResultsProps = {
  sections: CommandBarSection[]
  selectedIndex: number
  isSearching: boolean
  hasQuery: boolean
  onHoverItem: (index: number) => void
  onExecuteItem: (index: number) => void
}

export const CommandBarResults = ({
  sections,
  selectedIndex,
  isSearching,
  hasQuery,
  onHoverItem,
  onExecuteItem,
}: CommandBarResultsProps) => {
  const { t } = useTranslate('commandBar')
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const selected = listRef.current?.querySelector(
      '[data-command-bar-item][aria-selected="true"]'
    )
    selected?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  let runningIndex = -1

  return (
    <Box
      ref={listRef}
      role="listbox"
      sx={{
        flex: 1,
        overflowY: 'auto',
        py: 1,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255,255,255,0.18)',
          borderRadius: 999,
        },
      }}
    >
      {isSearching && hasQuery ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} sx={{ color: 'primary.main' }} />
        </Box>
      ) : null}

      {!isSearching && hasQuery && sections.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ px: 2, py: 3, color: 'rgba(255,255,255,0.45)' }}
        >
          {t('noResults')}
        </Typography>
      ) : null}

      {sections.map((section) => (
        <Box key={section.id} component="section">
          <Typography component="div" sx={commandBarSectionTitleSx}>
            {section.title}
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {section.items.map((item) => {
              runningIndex += 1
              const itemIndex = runningIndex
              return (
                <CommandBarItemRow
                  key={item.id}
                  item={item}
                  selected={itemIndex === selectedIndex}
                  recentLabel={t('recentBadge')}
                  onSelect={() => onHoverItem(itemIndex)}
                  onHover={() => onHoverItem(itemIndex)}
                  onExecute={() => onExecuteItem(itemIndex)}
                />
              )
            })}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
